import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { del, get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, humanize, timeOnly, todayIso } from '@/lib/utils';
import { FilterBar, FilterField } from '@/components/ui/layout';
import { Button, Select, StatusBadge, TableSkeleton } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { shiftIsoDate, weekStart } from '../opd/calendar/calendar-time';
import { rosterPath } from './NewRosterPage';
import { dayLabel, isoDay, LEAVE_TONE, SHIFT_TONE } from './hr-shared';

interface RosterSummary {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: string;
}

interface RosterShift {
  id: string;
  shiftDate: string;
  shift: string;
  startsAt: string;
  endsAt: string;
  employee: { id: string; name: string; employeeNo: string; designation: string | null };
  ward?: { id: string; code: string; name: string } | null;
}

interface RosterDetail extends RosterSummary {
  departmentId: string | null;
  shifts: RosterShift[];
}

interface EmployeeRow {
  id: string;
  name: string;
  employeeNo: string;
  designation: string | null;
}

interface ApprovedLeave {
  id: string;
  type: string;
  fromDate: string;
  toDate: string;
  employee: { id: string };
}

const SHIFT_ORDER: Record<string, number> = { morning: 0, evening: 1, night: 2 };

function defaultWeek(roster: RosterDetail | undefined): string {
  const today = todayIso();
  if (!roster) return weekStart(today);
  const start = isoDay(roster.periodStart);
  const end = isoDay(roster.periodEnd);
  return weekStart(today >= start && today <= end ? today : start);
}

function ShiftChip({ shift, onRemove }: { shift: RosterShift; onRemove?: () => void }) {
  return (
    <span
      className={cn(
        'group/chip relative flex flex-col rounded-md border px-1.5 py-0.5 text-[11px] leading-tight',
        SHIFT_TONE[shift.shift] ?? 'border-border bg-muted text-foreground',
      )}
    >
      <span className="font-semibold">{humanize(shift.shift)}</span>
      <span className="tabular">
        {timeOnly(shift.startsAt)}–{timeOnly(shift.endsAt)}
      </span>
      {shift.ward && (
        <span className="truncate" title={shift.ward.name}>
          {shift.ward.code}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${shift.shift} shift`}
          className="absolute -end-1 -top-1 hidden h-4 w-4 place-items-center rounded-full border bg-card text-muted-foreground shadow-sm hover:text-destructive focus-ring group-hover/chip:grid"
        >
          <X className="h-2.5 w-2.5" aria-hidden />
        </button>
      )}
    </span>
  );
}

export function RosterTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rosterId, setRosterId] = useState(() => params.get('roster') ?? '');
  const [weekOverride, setWeekOverride] = useState<string | null>(() => {
    const week = params.get('week');
    return week && /^\d{4}-\d{2}-\d{2}$/.test(week) ? weekStart(week) : null;
  });
  const manage = can('hr.roster.manage');

  const rosters = useQuery({
    queryKey: ['rosters'],
    queryFn: () => get<RosterSummary[]>('/hr/rosters'),
  });

  const roster = useQuery({
    queryKey: ['roster', rosterId],
    queryFn: () => get<RosterDetail>(`/hr/rosters/${rosterId}`),
    enabled: Boolean(rosterId),
  });

  const weekFrom = weekOverride ?? defaultWeek(roster.data);
  const days = Array.from({ length: 7 }, (_, index) => shiftIsoDate(weekFrom, index));
  const weekTo = days[6];
  const departmentId = roster.data?.departmentId ?? null;

  const employees = useQuery({
    queryKey: ['employees', departmentId ?? ''],
    queryFn: () => get<EmployeeRow[]>(`/hr/employees${qs({ departmentId: departmentId ?? undefined, limit: 300 })}`),
    enabled: roster.isSuccess,
  });

  const leave = useQuery({
    queryKey: ['leave', 'approved', weekFrom, weekTo],
    queryFn: () => get<ApprovedLeave[]>(`/hr/leave${qs({ status: 'approved', from: weekFrom, to: weekTo, limit: 500 })}`),
    enabled: roster.isSuccess,
  });

  const publish = useMutation({
    mutationFn: () => post(`/hr/rosters/${rosterId}/publish`),
    onSuccess: () => {
      toast.success('Roster published');
      void queryClient.invalidateQueries({ queryKey: ['roster', rosterId] });
      void queryClient.invalidateQueries({ queryKey: ['rosters'] });
    },
    onError: (error) => toast.error('Could not publish', describeError(error)),
  });

  const remove = useMutation({
    mutationFn: (shiftId: string) => del(`/hr/roster-shifts/${shiftId}`),
    onSuccess: () => {
      toast.success('Shift removed');
      void queryClient.invalidateQueries({ queryKey: ['roster', rosterId] });
    },
    onError: (error) => toast.error('Could not remove the shift', describeError(error)),
  });

  const chooseRoster = (id: string) => {
    setRosterId(id);
    setWeekOverride(null);
  };

  const shiftsByCell = new Map<string, RosterShift[]>();
  for (const shift of roster.data?.shifts ?? []) {
    const key = `${shift.employee.id}:${isoDay(shift.shiftDate)}`;
    shiftsByCell.set(key, [...(shiftsByCell.get(key) ?? []), shift]);
  }

  const people = new Map<string, EmployeeRow>();
  for (const employee of employees.data ?? []) people.set(employee.id, employee);
  for (const shift of roster.data?.shifts ?? []) if (!people.has(shift.employee.id)) people.set(shift.employee.id, shift.employee);
  const rows = [...people.values()].sort((a, b) => a.name.localeCompare(b.name));

  const leaveFor = (employeeId: string, day: string) =>
    leave.data?.find((row) => row.employee.id === employeeId && isoDay(row.fromDate) <= day && isoDay(row.toDate) >= day);

  const coverage = days.map((day) => {
    const counts: Record<string, number> = {};
    for (const shift of roster.data?.shifts ?? []) {
      if (isoDay(shift.shiftDate) === day) counts[shift.shift] = (counts[shift.shift] ?? 0) + 1;
    }
    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
    const detail = Object.entries(counts)
      .sort(([a], [b]) => (SHIFT_ORDER[a] ?? 9) - (SHIFT_ORDER[b] ?? 9))
      .map(([shift, n]) => `${n} ${shift}`)
      .join(' · ');
    return { day, total, detail };
  });

  const periodStart = roster.data ? isoDay(roster.data.periodStart) : '';
  const periodEnd = roster.data ? isoDay(roster.data.periodEnd) : '';
  const inPeriod = (day: string) => day >= periodStart && day <= periodEnd;
  const today = todayIso();
  const returnTo = `${rosterPath(rosterId)}&week=${weekFrom}`;

  const openAssign = (employeeId: string, day: string) =>
    navigate(`/people/rosters/${rosterId}/shifts/new${qs({ employeeId, date: day, returnTo })}`);

  return (
    <>
      <FilterBar
        trailing={
          <>
            {manage && (
              <Button variant="outline" onClick={() => navigate('/people/rosters/new')}>
                New roster
              </Button>
            )}
            {rosterId && manage && (
              <>
                <Button variant="outline" onClick={() => navigate(`/people/rosters/${rosterId}/shifts/new${qs({ returnTo })}`)}>
                  Assign shift
                </Button>
                {roster.data?.status === 'draft' && (
                  <Button onClick={() => publish.mutate()} loading={publish.isPending}>
                    Publish
                  </Button>
                )}
              </>
            )}
          </>
        }
      >
        <FilterField label="Roster">
          <Select className="w-72" value={rosterId} onChange={(e) => chooseRoster(e.target.value)}>
            <option value="">Select a roster…</option>
            {rosters.data?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({dateOnly(r.periodStart)} → {dateOnly(r.periodEnd)})
              </option>
            ))}
          </Select>
        </FilterField>
        {rosterId && (
          <FilterField label="Week">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" aria-label="Previous week" onClick={() => setWeekOverride(shiftIsoDate(weekFrom, -7))}>
                <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
              </Button>
              <span className="min-w-[11rem] text-center text-sm tabular">
                {dayLabel(weekFrom, { day: 'numeric', month: 'short' })} – {dayLabel(weekTo, { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
              <Button variant="outline" size="icon" aria-label="Next week" onClick={() => setWeekOverride(shiftIsoDate(weekFrom, 7))}>
                <ChevronRight className="h-4 w-4 rtl:rotate-180" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekOverride(weekStart(today))}>
                This week
              </Button>
            </div>
          </FilterField>
        )}
        {roster.data && (
          <FilterField label="Status">
            <div className="flex h-9 items-center">
              <StatusBadge status={roster.data.status} />
            </div>
          </FilterField>
        )}
      </FilterBar>

      {!rosterId && (
        <div className="rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground">Pick a roster to see its week board.</div>
      )}

      {rosterId && (roster.isLoading || employees.isLoading) && <TableSkeleton columns={8} rows={6} />}

      {rosterId && roster.error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">{describeError(roster.error)}</div>
      )}

      {roster.data && employees.data && (
        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/60">
                  <th scope="col" className="sticky start-0 z-10 min-w-[12rem] bg-muted/60 px-3 py-2 text-start text-xs font-semibold text-muted-foreground backdrop-blur">
                    Person
                  </th>
                  {days.map((day) => (
                    <th
                      key={day}
                      scope="col"
                      className={cn(
                        'min-w-[7.5rem] px-2 py-2 text-center text-xs font-semibold text-muted-foreground',
                        day === today && 'bg-primary/5 text-primary',
                        !inPeriod(day) && 'opacity-50',
                      )}
                    >
                      {dayLabel(day)}
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-border/70 bg-muted/30">
                  <th scope="row" className="sticky start-0 z-10 bg-muted/30 px-3 py-1.5 text-start text-[11px] font-medium uppercase tracking-wider text-muted-foreground backdrop-blur">
                    Coverage
                  </th>
                  {coverage.map((cell) => (
                    <td key={cell.day} className={cn('px-2 py-1.5 text-center text-xs tabular', cell.day === today && 'bg-primary/5')} title={cell.detail || 'Nobody rostered'}>
                      {cell.total === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="font-medium">
                          {cell.total} on shift
                          {cell.detail && <span className="block font-normal text-muted-foreground">{cell.detail}</span>}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      No active employees in this roster&apos;s department.
                    </td>
                  </tr>
                )}
                {rows.map((employee) => (
                  <tr key={employee.id} className="border-b border-border/70 last:border-0 odd:bg-muted/20">
                    <th scope="row" className="sticky start-0 z-10 bg-card px-3 py-2 text-start font-medium backdrop-blur">
                      <p>{employee.name}</p>
                      <p className="text-xs font-normal text-muted-foreground">{employee.designation ?? employee.employeeNo}</p>
                    </th>
                    {days.map((day) => {
                      const shifts = (shiftsByCell.get(`${employee.id}:${day}`) ?? []).sort(
                        (a, b) => (SHIFT_ORDER[a.shift] ?? 9) - (SHIFT_ORDER[b.shift] ?? 9),
                      );
                      const onLeave = shifts.length === 0 ? leaveFor(employee.id, day) : undefined;
                      const assignable = manage && inPeriod(day) && !onLeave;
                      return (
                        <td key={day} className={cn('group/cell align-top p-1.5', day === today && 'bg-primary/5')}>
                          <div className="flex min-h-[2.75rem] flex-col gap-1">
                            {shifts.map((shift) => (
                              <ShiftChip key={shift.id} shift={shift} onRemove={manage ? () => remove.mutate(shift.id) : undefined} />
                            ))}
                            {onLeave && (
                              <span className={cn('flex flex-col rounded-md border px-1.5 py-0.5 text-[11px] leading-tight', LEAVE_TONE)}>
                                <span className="font-semibold">On leave</span>
                                <span>{humanize(onLeave.type)}</span>
                              </span>
                            )}
                            {assignable && (
                              <button
                                type="button"
                                onClick={() => openAssign(employee.id, day)}
                                aria-label={`Assign ${employee.name} a shift on ${dayLabel(day)}`}
                                className={cn(
                                  'grid h-7 w-full place-items-center rounded-md border border-dashed border-transparent text-muted-foreground/40 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary focus-ring',
                                  shifts.length > 0 && 'h-5 opacity-0 group-hover/cell:opacity-100',
                                )}
                              >
                                <Plus className="h-3.5 w-3.5" aria-hidden />
                              </button>
                            )}
                            {!assignable && shifts.length === 0 && !onLeave && (
                              <span className="grid h-7 place-items-center text-xs text-muted-foreground/40">{inPeriod(day) ? '—' : ''}</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span>
              Roster period {dateOnly(roster.data.periodStart)} → {dateOnly(roster.data.periodEnd)}
            </span>
            <span>{roster.data.shifts.length} shifts assigned</span>
            {['morning', 'evening', 'night'].map((shift) => (
              <span key={shift} className="inline-flex items-center gap-1.5">
                <span className={cn('h-2.5 w-2.5 rounded-sm border', SHIFT_TONE[shift])} aria-hidden />
                {humanize(shift)}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className={cn('h-2.5 w-2.5 rounded-sm border', LEAVE_TONE)} aria-hidden />
              Approved leave
            </span>
          </div>
        </div>
      )}
    </>
  );
}
